import { Add, Close, ContentCopy, Create, EditNote, KeyboardArrowDown } from "@mui/icons-material";
import { Button, CircularProgress, InputBase, Snackbar } from "@mui/material";
import { CheckCircle, ArrowDown, ArrowDownLeft, ArrowDownRight } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import IncommingPhonenumber from "../RouteParts/IncommingPhonenumber";
import BusinessHours from "../RouteParts/BusinessHours";
import CallAction from "../RouteParts/CallAction";
import Notification from "../RouteParts/Notification";
import axios from "axios";
import posthog from "posthog-js";
import OffHoursCallAction from "../RouteParts/OffHoursCallAction";
import PrimaryCallAction from "../RouteParts/PrimaryCallAction";
import PrimaryNotification from "../RouteParts/PrimaryNotification";
import OffHoursNotification from "../RouteParts/offHoursNotification";
import { useNavigate, useParams } from "react-router-dom";
import planData from "../plans.json";
import DeleteRouteModal from "../modals/DeleteRouteModal";
import RouteValidationWarningModal from "../modals/RouteValidationWarningModal";
import { validateRoute } from "../RouteParts/routeValidation";
import {
  normalizeRouteBusinessHoursForApi,
  normalizeIvrOptionsMap,
  omitIvrMenuBusinessHoursField,
} from "../RouteParts/routePayloadNormalize";
import { RouteConfigModalsProvider } from "../contexts/RouteConfigModalsContext";
import LoadingAnimation from "../reusables/LoadingAnimation";
import { getVoiceUrlOldNumberUpdatedMessage } from "../utils/voiceUrlSyncNotice";

const NewCallRoute = ({navigate}) => {
  const [editName, setEditName] = useState(false);
  const [openSnack, setOpenSnack] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [loader, setLoader] = useState(false);
  const [openDeleteRouteModal, setOpenDeleteRouteModal] = useState(false);
  const [openValidationModal, setOpenValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]); // { message, section }[]
  const [pendingSaveAction, setPendingSaveAction] = useState(null); // 'create' | 'update'
  const [highlightValidationErrors, setHighlightValidationErrors] = useState(false);
  const [copied, setCopied] = useState(false);

  const validationErrorSections = useMemo(
    () => new Set(validationErrors.map((e) => e?.section).filter(Boolean)),
    [validationErrors]
  );

  const orgFromStorage = JSON.parse(localStorage.getItem("organization") || "{}");
  const primaryPhoneFromOrg = orgFromStorage?.twilio_integration?.M?.primary_phone?.S ?? null;
  const fromOnboarding = typeof window !== "undefined" && localStorage.getItem("fromOnboarding") === "true";

  const [route, setRoute] = useState({
    name: "My New Route",
    configured_phone: fromOnboarding ? primaryPhoneFromOrg : null,
    org_id: orgFromStorage?.orgId?.S,
    recordCall: false, // boolean: false = disabled, true = enabled
    transcribeCall: false, // boolean: false = disabled, true = enabled
    callGreetingEnabled: false,
    callGreetingRecording: "",
  });

  function parseDynamoItem(item) {
    if (!item || typeof item !== "object") return item;
  
    // Handle DynamoDB AttributeValue format
    if (item.S !== undefined) return item.S;
    if (item.N !== undefined) return Number(item.N);
    if (item.BOOL !== undefined) return item.BOOL;
    if (item.NULL) return null;
  
    if (item.L) return item.L.map(parseDynamoItem);
    if (item.M) {
      const result = {};
      for (const [key, value] of Object.entries(item.M)) {
        result[key] = parseDynamoItem(value);
      }
      return result;
    }
  
    // In case it's already a JS object (not DynamoDB format)
    const result = {};
    for (const [key, value] of Object.entries(item)) {
      result[key] = parseDynamoItem(value);
    }
    return result;
  }

  const { route_id } = useParams();

  const copyRouteUrl = useCallback(async () => {
    const url = `https://incoming.twilanswer.com/${route_id || route?.routeId || "your-new-route-id"}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy route URL:", err);
    }
  }, [route_id, route?.routeId]);

  const [planDetails, setPlanDetails] = useState(null);
  const check_subscription = async () => {
    try {
      setLoader(true);
      let sub = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "stripe/check-subscription",
        {
          organization_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
          customer_id: JSON.parse(localStorage.getItem("organization"))
            .subscription_details.M.stripe_cust_id.S,
        }
      );

      setPlanDetails(planData[sub.data?.planDetails[0].plan_id]);
      setLoader(false);
      
    } catch (error) {
      // Log for production diagnostics when subscription check fails
      console.error("NewCallRoute: check subscription failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setOpenSnack(true)
        setSnackMsg(error.response.data.message ?? error.response.data.error ?? "Something went wrong");
      }
      setLoader(false);
    }
  };


  const getRoute = async () => {
    try {
      let route = await axios.post(
        process.env.REACT_APP_BACKEND_URL + "route/get-route",
        { route_id, org_id: JSON.parse(localStorage.getItem("organization")).orgId.S }
      );

      let r = route?.data.route;

      let m = parseDynamoItem(r);
      if (m.calendar) m.calendar = normalizeCalendarKeysToAbbreviated(m.calendar);
      if (m.ivrMenu?.options) {
        m.ivrMenu.options = normalizeIvrOptionsMap(m.ivrMenu.options);
        m.ivrMenu.options = Object.fromEntries(Object.entries(m.ivrMenu.options).map(([k, opt]) => [
          k, { ...opt, calendar: opt?.calendar ? normalizeCalendarKeysToAbbreviated(opt.calendar) : opt?.calendar },
        ]));
      }
      if (m.offHoursIvrMenu?.options) {
        m.offHoursIvrMenu.options = normalizeIvrOptionsMap(m.offHoursIvrMenu.options);
        m.offHoursIvrMenu.options = Object.fromEntries(Object.entries(m.offHoursIvrMenu.options).map(([k, opt]) => [
          k, { ...opt, calendar: opt?.calendar ? normalizeCalendarKeysToAbbreviated(opt.calendar) : opt?.calendar },
        ]));
      }
      // Normalize to camelCase for frontend (backend may return primary_action / offhours_action)
      if (m.primary_action != null && m.primaryAction == null) m.primaryAction = m.primary_action;
      if (m.offhours_action != null && m.offHoursAction == null) m.offHoursAction = m.offhours_action;
      // Normalize primaryChannel/offHoursChannel to channel IDs (legacy routes may have stored values)
      const channels = JSON.parse(localStorage.getItem("organization") || "{}")?.channels?.L || [];
      const valueToChId = (val) => {
        if (!val) return val;
        const ch = channels.find((c) => (c?.M?.ch_id?.S ?? c?.ch_id) === val);
        if (ch) return ch?.M?.ch_id?.S ?? ch?.ch_id;
        const byValue = channels.find((c) => (c?.M?.value?.S ?? c?.value) === val);
        return byValue ? (byValue?.M?.ch_id?.S ?? byValue?.ch_id) : val;
      };
      if (m.primaryChannel) m.primaryChannel = valueToChId(m.primaryChannel);
      if (m.offHoursChannel) m.offHoursChannel = valueToChId(m.offHoursChannel);
      if (m.ivrMenu?.options) {
        m.ivrMenu.options = Object.fromEntries(
          Object.entries(m.ivrMenu.options).map(([k, opt]) => [
            k,
            {
              ...opt,
              ...(opt.primaryChannel && { primaryChannel: valueToChId(opt.primaryChannel) }),
              ...(opt.offHoursChannel && { offHoursChannel: valueToChId(opt.offHoursChannel) }),
            },
          ])
        );
      }
      if (m.offHoursIvrMenu?.options) {
        m.offHoursIvrMenu.options = Object.fromEntries(
          Object.entries(m.offHoursIvrMenu.options).map(([k, opt]) => [
            k,
            {
              ...opt,
              ...(opt.primaryChannel && { primaryChannel: valueToChId(opt.primaryChannel) }),
              ...(opt.offHoursChannel && { offHoursChannel: valueToChId(opt.offHoursChannel) }),
            },
          ])
        );
      }
      if (m.ivrMenu) m.ivrMenu = omitIvrMenuBusinessHoursField(m.ivrMenu);
      if (m.offHoursIvrMenu) m.offHoursIvrMenu = omitIvrMenuBusinessHoursField(m.offHoursIvrMenu);
      const rawOrg = JSON.parse(localStorage.getItem("organization") || "{}");
      const orgIdVal = rawOrg?.orgId ?? rawOrg?.org_id;
      const orgIdStr = typeof orgIdVal === "string" ? orgIdVal : orgIdVal?.S ?? "";
      if (m.org_id === orgIdStr) {
       if (m.recordCall === undefined) {
         console.warn("NewCallRoute: get-route response missing recordCall; assuming recording disabled.");
       }
       const recordCallVal = m.recordCall !== undefined ? Boolean(m.recordCall) : false;
       if (m.transcribeCall === undefined) {
         console.warn("NewCallRoute: get-route response missing transcribeCall; assuming transcription disabled.");
       }
       const transcribeCallVal = m.transcribeCall !== undefined ? Boolean(m.transcribeCall) : false;
       const businessHoursEnabled =
         m.businessHoursEnabled === true || m.businessHoursEnabled === false
           ? m.businessHoursEnabled
           : Object.values(m.calendar || {}).some((day) => day?.active === true);
       setRoute({
         ...m,
         businessHoursEnabled,
         recordCall: recordCallVal,
         transcribeCall: transcribeCallVal,
         callGreetingEnabled: !!(m.callGreetingRecording),
         callGreetingRecording: m.callGreetingRecording ?? "",
       });
       setEnableBusinessHours(businessHoursEnabled);
       setLoader(false);
     }else{
        setOpenSnack(true)
        setSnackMsg('you cant view this route');
        setLoader(false);
     }
    } catch (error) {
      // Log for production diagnostics when get route fails
      console.error("NewCallRoute: get route failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setOpenSnack(true)
        setSnackMsg(error.response.data.message ?? error.response.data.error ?? "Something went wrong");
      }
      setLoader(false);
    }
  };

  useEffect(() => {
    if (route_id) {
        setLoader(true);
        check_subscription();
        getRoute();
    }
  },[route_id]);

  // Clear onboarding flag after use so it only applies to the first new route after onboarding
  useEffect(() => {
    if (!route_id && localStorage.getItem("fromOnboarding") === "true") {
      localStorage.removeItem("fromOnboarding");
    }
  }, [route_id]);

  // When creating a new route (no route_id), set default name to "Route N" based on existing route count
  useEffect(() => {
    if (!route_id) {
      const fetchRouteCountAndSetName = async () => {
        try {
          const res = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "route/get-routes",
            { org_id: JSON.parse(localStorage.getItem("organization")).orgId.S }
          );
          const routes = res.data.routes;
          const count = Array.isArray(routes) ? routes.length : 0;
          setRoute((prev) => ({
            ...prev,
            name: `Route ${count + 1}`,
          }));
        } catch (err) {
          // Log for production diagnostics when fetching route count for default name fails
          console.error("NewCallRoute: fetch route count for default name failed", err.response?.data || err.message || err);
          setRoute((prev) => ({
            ...prev,
            name: "Route 1",
          }));
        }
      };
      fetchRouteCountAndSetName();
    }
  }, [route_id]);
  const createNewRoute = async (forceSave = false) => {
    if (!forceSave) {
      const { errors, isValid } = validateRoute(route, enableBusinessHours);
      if (!isValid) {
        setValidationErrors(errors);
        setOpenValidationModal(true);
        setPendingSaveAction("create");
        return;
      }
    }
    setHighlightValidationErrors(false);

    setLoader(true);

    // Enforce organization policy: if org disallows recording/transcription, ensure route settings respect that
    const organization = JSON.parse(localStorage.getItem("organization"));
    const recordingConfig = organization?.recording_transcription_config?.M || {};
    const inboundRecordingPolicy = recordingConfig.inbound_recording?.S || "disallow";
    const inboundTranscriptionPolicy = recordingConfig.inbound_transcription?.S || "disallow";
    
    const routeToSave = { ...route };
    
    // Enforce recording policy
    if (inboundRecordingPolicy === "disallow") {
      routeToSave.recordCall = false;
    } else if (inboundRecordingPolicy === "require") {
      routeToSave.recordCall = true;
    }
    
    // Enforce transcription policy
    if (inboundTranscriptionPolicy === "disallow" || inboundRecordingPolicy === "disallow") {
      routeToSave.transcribeCall = false;
    } else if (inboundTranscriptionPolicy === "require") {
      routeToSave.transcribeCall = true;
    } else if (!routeToSave.recordCall) {
      // If recording is disabled, transcription must also be disabled
      routeToSave.transcribeCall = false;
    }

    let normalizedRoute = normalizeRouteCalendarFields(routeToSave);
    normalizedRoute = normalizeRouteBusinessHoursForApi(normalizedRoute, enableBusinessHours);
    const { callGreetingEnabled, ...routePayload } = normalizedRoute;
    const routePayloadForApi = normalizeRouteActionKeys(routePayload);

    let check = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
      {
        userId: JSON.parse(localStorage.getItem("user")).userId.S,
      }
    );

    if (check.status === 200) {
      const user___ = check.data.user;

      if (
        user___.relatedOrgs.L?.find(
          (e) =>
            e?.M.org_id?.S ===
            JSON.parse(localStorage.getItem("organization")).orgId.S
        )
      ) {
        try {
          let route_data = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "route/create-route",
            routePayloadForApi
          );

          let organization = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "add-route",
            {
              organization_id: JSON.parse(localStorage.getItem("organization"))
                .orgId.S,
              route_id: route_data.data.route.routeId.S,
            }
          );

          localStorage.setItem(
            "organization",
            JSON.stringify(organization.data.organization)
          );

          let t = JSON.parse(localStorage.getItem("organization"))
            .twilio_integration.M;

          if (t.auto_configuration.BOOL) {
            let update_voice = await axios.post(
              process.env.REACT_APP_BACKEND_URL + "twilio/update-voice-url",
              {
                org_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
                phone_number: route.configured_phone,
                webhook_url: `https://incoming.twilanswer.com/${route_data.data.route.routeId.S}`,
              }
            );
          }
          setLoader(false);

          setOpenSnack(true);
          setSnackMsg("Route saved successfully. Redirecting you now.");
          window.scrollTo({ top: 0, behavior: "smooth" });
          //post hog track event for create route
          posthog.capture("Created a route", {
            email: JSON.parse(localStorage.getItem("user")).email.S,
            userId: JSON.parse(localStorage.getItem("user")).userId.S,
          });

             setTimeout(() => {
               navigate(
                 "/dashboard/call-route/" + route_data.data.route.routeId.S
               );
             }, 2000);
        } catch (error) {
          // Log for production diagnostics when create route fails
          console.error("NewCallRoute: create route failed", error.response?.data || error.message || error);
          if (error.response && error.response.data) {
            setOpenSnack(true);
            setSnackMsg(error.response.data.message ?? error.response.data.error ?? "Something went wrong");
          }
          setLoader(false);
        }
      } else {
        window.location.reload();
      }
    }
  };


   async function deleteRoute() {
      setLoader(true);
      setOpenDeleteRouteModal(false);
  
      let check = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
        {
          userId: JSON.parse(localStorage.getItem("user")).userId.S,
        }
      );
  
      if (check.status === 200) {
        const user___ = check.data.user;
  
        if (
          user___.relatedOrgs.L?.find(
            (e) =>
              e?.M.org_id?.S ===
              JSON.parse(localStorage.getItem("organization")).orgId.S
          )
        ) {
          try {
            let route_data = await axios.post(
              process.env.REACT_APP_BACKEND_URL + "route/delete-route",
              { route_id, org_id: JSON.parse(localStorage.getItem("organization")).orgId.S }
            );
  
            let organization = await axios.post(
              process.env.REACT_APP_BACKEND_URL + "delete-route",
              {
                organization_id: JSON.parse(localStorage.getItem("organization"))
                  .orgId.S,
                route_id,
              }
            );
  
            localStorage.setItem(
              "organization",
              JSON.stringify(organization.data.organization)
            );
  
            let t = JSON.parse(localStorage.getItem("organization"))
              .twilio_integration.M;
  
            if (t.auto_configuration.BOOL) {
              let update_voice = await axios.post(
                process.env.REACT_APP_BACKEND_URL + "twilio/update-voice-url",
                {
                  org_id: JSON.parse(localStorage.getItem("organization")).orgId.S,
                  phone_number: route.configured_phone,
                  webhook_url: `https://incoming.twilanswer.com/default`,
                }
              );
            }
            setLoader(false);
            setOpenSnack(true)
            setSnackMsg(
              "Route Successfully Deleted. Remember to Update Twilio Settings. Redirecting you to your call routes"
            );
            setTimeout(() => {
              navigate("/dashboard/call-routings/");
            }, 4000);
          } catch (error) {
            // Log for production diagnostics when delete route fails
            console.error("NewCallRoute: delete route failed", error.response?.data || error.message || error);
            if (error.response && error.response.data) {
              setSnackMsg(error.response.data.message ?? error.response.data.error ?? "Something went wrong");
              setOpenSnack(true)
            }
            setLoader(false);
          }
        } else {
          window.location.reload();
        }
      }
    }
  const [enableBusinessHours, setEnableBusinessHours] = useState(false);

  /** Map full day names to abbreviated keys (backward compat when loading old routes). */
  const CALENDAR_DAY_MAP = { monday: "mon", tuesday: "tue", wednesday: "wed", thursday: "thur", friday: "fri", saturday: "sat", sunday: "sun" };
  function normalizeCalendarKeysToAbbreviated(calendar) {
    if (!calendar || typeof calendar !== "object") return calendar;
    const out = {};
    for (const [day, dayConfig] of Object.entries(calendar)) {
      const key = CALENDAR_DAY_MAP[day] ?? day;
      out[key] = dayConfig;
    }
    return out;
  }

  /** Normalize calendar day rules to use earlyTime/lateTime (camelCase) for API. */
  function normalizeCalendarToEarlyTime(calendar) {
    if (!calendar || typeof calendar !== "object") return calendar;
    const out = {};
    for (const [day, dayConfig] of Object.entries(calendar)) {
      if (!dayConfig || typeof dayConfig !== "object") {
        out[day] = dayConfig;
        continue;
      }
      out[day] = { ...dayConfig };
      out[day].earlyTime = dayConfig.earlyTime ?? dayConfig.early_time;
      out[day].lateTime = dayConfig.lateTime ?? dayConfig.late_time;
      delete out[day].early_time;
      delete out[day].late_time;
    }
    return out;
  }

  /** Ensure route payload uses earlyTime/lateTime for all calendar rules. */
  function normalizeRouteCalendarFields(routePayload) {
    const r = { ...routePayload };
    if (r.calendar) r.calendar = normalizeCalendarToEarlyTime(r.calendar);
    r.ivrMenu = r.ivrMenu ? { ...r.ivrMenu } : r.ivrMenu;
    if (r.ivrMenu?.options && typeof r.ivrMenu.options === "object" && !Array.isArray(r.ivrMenu.options)) {
      r.ivrMenu.options = Object.fromEntries(
        Object.entries(r.ivrMenu.options).map(([k, opt]) => [
          k,
          { ...opt, calendar: normalizeCalendarToEarlyTime(opt.calendar) },
        ])
      );
    }
    r.offHoursIvrMenu = r.offHoursIvrMenu ? { ...r.offHoursIvrMenu } : r.offHoursIvrMenu;
    if (r.offHoursIvrMenu?.options && typeof r.offHoursIvrMenu.options === "object" && !Array.isArray(r.offHoursIvrMenu.options)) {
      r.offHoursIvrMenu.options = Object.fromEntries(
        Object.entries(r.offHoursIvrMenu.options).map(([k, opt]) => [
          k,
          { ...opt, calendar: normalizeCalendarToEarlyTime(opt.calendar) },
        ])
      );
    }
    return r;
  }

  /** Ensure route payload uses camelCase action keys for API (strip any legacy snake_case). */
  function normalizeRouteActionKeys(payload) {
    const p = { ...payload };
    p.primaryAction = p.primaryAction ?? p.primary_action ?? null;
    p.offHoursAction = p.offHoursAction ?? p.offhours_action ?? null;
    delete p.primary_action;
    delete p.offhours_action;
    return p;
  }

  const hasActiveCalendarDays = (calendar) => {
    if (!calendar || typeof calendar !== "object") return false;
  
    return Object.values(calendar).some((day) => day?.active === true);
  };

  useEffect(() => {
    if (route?.businessHoursEnabled === true || route?.businessHoursEnabled === false) {
      setEnableBusinessHours(route.businessHoursEnabled);
    } else if (hasActiveCalendarDays(route?.calendar)) {
      setEnableBusinessHours(true);
    }
  }, [route?.businessHoursEnabled, route?.calendar]);

  // When highlight mode is on, re-run validation on route changes so the red tint clears as soon as the user fixes an issue
  useEffect(() => {
    if (!highlightValidationErrors) return;
    const { errors, isValid } = validateRoute(route, enableBusinessHours);
    setValidationErrors(errors);
    if (isValid) setHighlightValidationErrors(false);
  }, [route, enableBusinessHours, highlightValidationErrors]);

  async function updateRoute(forceSave = false) {
      if (!forceSave) {
        const { errors, isValid } = validateRoute(route, enableBusinessHours);
        if (!isValid) {
          setValidationErrors(errors);
          setOpenValidationModal(true);
          setPendingSaveAction("update");
          return;
        }
      }
      setHighlightValidationErrors(false);

      setLoader(true);
      
      // Enforce organization policy: if org disallows recording/transcription, ensure route settings respect that
      const organization = JSON.parse(localStorage.getItem("organization"));
      const recordingConfig = organization?.recording_transcription_config?.M || {};
      const inboundRecordingPolicy = recordingConfig.inbound_recording?.S || "disallow";
      const inboundTranscriptionPolicy = recordingConfig.inbound_transcription?.S || "disallow";
      
      let routeToSave = { ...route };
      
      // Enforce recording policy
      if (inboundRecordingPolicy === "disallow") {
        routeToSave.recordCall = false;
      } else if (inboundRecordingPolicy === "require") {
        routeToSave.recordCall = true;
      }
      
      // Enforce transcription policy
      if (inboundTranscriptionPolicy === "disallow" || inboundRecordingPolicy === "disallow") {
        routeToSave.transcribeCall = false;
      } else if (inboundTranscriptionPolicy === "require") {
        routeToSave.transcribeCall = true;
      } else if (!routeToSave.recordCall) {
        // If recording is disabled, transcription must also be disabled
        routeToSave.transcribeCall = false;
      }

      routeToSave = normalizeRouteCalendarFields(routeToSave);
      routeToSave = normalizeRouteBusinessHoursForApi(routeToSave, enableBusinessHours);
      const { callGreetingEnabled: _cgEnabled, ...updatePayload } = routeToSave;
      const updatePayloadForApi = normalizeRouteActionKeys(updatePayload);

      let check = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}get-user-by-id`,
        {
          userId: JSON.parse(localStorage.getItem("user")).userId.S,
        }
      );
  
      if (check.status === 200) {
        const user___ = check.data.user;
  
        if (
          user___.relatedOrgs.L?.find(
            (e) =>
              e?.M.org_id?.S ===
              JSON.parse(localStorage.getItem("organization")).orgId.S
          )
        ) {
          try {
            let route_data = await axios.post(
              process.env.REACT_APP_BACKEND_URL + "route/update-route",
              { ...updatePayloadForApi, org_id: JSON.parse(localStorage.getItem("organization")).orgId.S }
            );

            setLoader(false);
            getRoute();
            const baseSuccess = "Route Settings Successfully Updated";
            const voiceFollowUp = getVoiceUrlOldNumberUpdatedMessage(
              route_data.data?.voiceUrlSync,
              { newPhoneNumber: route.configured_phone }
            );
            setSnackMsg(
              voiceFollowUp ? `${baseSuccess}\n\n${voiceFollowUp}` : baseSuccess
            );
            setOpenSnack(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
            
          } catch (error) {
            // Log for production diagnostics when update route fails
            console.error("NewCallRoute: update route failed", error.response?.data || error.message || error);
            if (error.response && error.response.data) {
              setSnackMsg(error.response.data.message ?? error.response.data.error ?? "Something went wrong");
            setOpenSnack(true)
            }
            setLoader(false);
          }
        } else {
          window.location.reload();
        }
      }
    }
  return (
    <RouteConfigModalsProvider planDetails={planDetails}>
    <div>
    <DeleteRouteModal
          open={openDeleteRouteModal}
          setOpen={setOpenDeleteRouteModal}
          num={route.configured_phone}
          deleteRoute={deleteRoute}
          routeName={route?.name}
        />
        <RouteValidationWarningModal
          open={openValidationModal}
          onClose={() => {
            setOpenValidationModal(false);
            setPendingSaveAction(null);
            setHighlightValidationErrors(true);
          }}
          errors={validationErrors.map((e) => (typeof e === "string" ? e : e?.message)).filter(Boolean)}
          onSaveAnyway={() => {
            setOpenValidationModal(false);
            setValidationErrors([]);
            setHighlightValidationErrors(false);
            if (pendingSaveAction === "create") createNewRoute(true);
            else if (pendingSaveAction === "update") updateRoute(true);
            setPendingSaveAction(null);
          }}
          loading={loader}
        />
      {route_id && loader ? (
        <div className="flex flex-col justify-center items-center py-16">
          <LoadingAnimation style={{ width: 64, height: 64 }} alt="Loading route" />
        </div>
      ) : (
      <>
      <div className="lg:flex items-center mb-4 lg:space-x-8 lg:space-y-0 space-y-3 relative py-4 px-4">
        <div className="flex items-center lg:space-x-3 space-x-0 justify-between">
          {/* {loader && (
            <div className="flex justify-center">
              <CircularProgress
                size={"1.0rem"}
                color="primary" sx={{ animationDuration: "0.7s" }}
              />
            </div>
          )} */}
          <div className="text-[17px] font-semibold ">
            {editName ? (
              <InputBase
                value={route?.name}
                onChange={(e) => {
                  setRoute({ ...route, name: e.target.value });
                }}
                sx={{
                  bgcolor: "white",
                  width: "100%",
                  px: 2,
                  py: "2px",
                  fontSize: "14px",
                }}
              />
            ) : (
              <>{route?.name}</>
            )}
          </div>

          <div className="cursor-pointer">
            {editName ? (
              <div
                className="cursor-pointer hover:underline hover:text-purple-500 text-[14px]"
                onClick={() => {
                  setEditName(false);
                }}
              >
                Save{" "}
              </div>
            ) : (
              <Create
                sx={{ color: "gray", width: "25px" }}
                onClick={() => {
                  setEditName(true);
                }}
              />
            )}
          </div>
        </div>
        <div className="bg-[#FAFAFB] py-2 text-[12px] px-4 text-center font-semibold rounded-[4px] break-words border-gray-200 border-[2px]">
          <span className="align-middle">https://incoming.twilanswer.com/{ route_id || route?.routeId || 'your-new-route-id'}</span>
          <span className="inline-block align-middle ml-1">
            {copied ? (
              <CheckCircle
                className="cursor-pointer"
                onClick={copyRouteUrl}
                style={{
                  color: "gray",
                  width: 16,
                  height: 16,
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
            ) : (
              <ContentCopy
                className="cursor-pointer"
                onClick={copyRouteUrl}
                sx={{
                  color: "gray",
                  width: "16px",
                  height: "16px",
                  display: "inline-block",
                  verticalAlign: "middle",
                }}
              />
            )}
          </span>
        </div>

        <div className=" lg:absolute flex justify-center  right-4">
        {loader ? (
            <div className="flex justify-center">
              <CircularProgress
                size={"1.0rem"}
                color="primary" sx={{ animationDuration: "0.7s" }}
              />
            </div>
          ): (
            <div className="flex items-center space-x-3">
            {route.routeId && (
              <Button
                sx={{
                  textTransform: "none",
                  border: " 1px solid #D11A2A",
                  color: "#D11A2A",
                  bgcolor: "white",
                  py: "5px",
                  px: "10px",
                  borderRadius: "6px",
                  fontSize: { lg: "14px", xs: "12px" },
                  fontWeight: 600
                }}
                disabled={loader}
                onClick={()=> {
                    setOpenDeleteRouteModal(true)
                }}
                startIcon={<Close color="error" sx={{ width:'14px', position:'relative', top:'0.4px'}} />}
              >
                Delete Route
              </Button>
            )}
            {route.routeId ? (
              <Button
                variant="contained"
                color="primary"
                sx={{
                  py: "8px",
                  px: "16px",
                  borderRadius: "6px",
                  fontSize: { lg: "14px", xs: "12px" },
                  "&:disabled": {
                    bgcolor: "#9CA3AF",
                    color: "white"
                  }
                }}
                disabled={loader}
                onClick={() => {
                  updateRoute();
                }}
              >
                Save Changes
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                sx={{
                  py: "8px",
                  px: "16px",
                  borderRadius: "6px",
                  fontSize: { lg: "14px", xs: "12px" },
                  "&:disabled": {
                    bgcolor: "#9CA3AF",
                    color: "white"
                  }
                }}
                disabled={loader}
                onClick={() => {
                  createNewRoute();
                }}
              >
                Save Route
              </Button>
            )}
          </div>
          )}
         
        </div>
      </div>

      <div className="flex flex-col items-center justify-center w-full px-3 space-y-5 ">
        <IncommingPhonenumber
          route={route}
          setRoute={setRoute}
          setOpenSnack={setOpenSnack}
          setSnackMsg={setSnackMsg}
          setLoader={setLoader}
          hasValidationError={highlightValidationErrors && (validationErrorSections.has("call_greeting") || validationErrorSections.has("configured_phone"))}
        />
        
        {/* Flow Arrow: Incoming Call → Business Hours */}
        <div className="flex justify-center">
          <ArrowDown size={16} className="text-gray-400" />
        </div>
        
        <BusinessHours
          route={route}
          setRoute={setRoute}
          setOpenSnack={setOpenSnack}
          setSnackMsg={setSnackMsg}
          setLoader={setLoader}
          enableBusinessHours={enableBusinessHours}
          setEnableBusinessHours={setEnableBusinessHours}
        />

        {/* Flow Arrows: Business Hours → Call Actions */}
        <div className="flex justify-center -mt-4">
          {enableBusinessHours ? (
            <div className="flex justify-between w-16">
              <ArrowDownLeft size={16} className="text-gray-400" />
              <ArrowDownRight size={16} className="text-gray-400" />
            </div>
          ) : (
            <ArrowDown size={16} className="text-gray-400" />
          )}
        </div>
        
        {enableBusinessHours ? (
          <div className="   lg:w-full lg:px-10 w-full ">
            <div className="text-lg font-semibold text-gray-900 mb-2">
              Call Action
            </div>
            <div className="lg:grid grid-cols-2 gap-2 space-y-2 lg:space-y-0 items-start   ">
              <PrimaryCallAction
                route={route}
                setRoute={setRoute}
                setOpenSnack={setOpenSnack}
                setSnackMsg={setSnackMsg}
                setLoader={setLoader}
                enableBusinessHours={enableBusinessHours}
                setEnableBusinessHours={setEnableBusinessHours}
                hasValidationError={highlightValidationErrors && (validationErrorSections.has("primary_action") || validationErrorSections.has("ivr_menu"))}
              />
              <OffHoursCallAction
                route={route}
                setRoute={setRoute}
                setOpenSnack={setOpenSnack}
                setSnackMsg={setSnackMsg}
                setLoader={setLoader}
                enableBusinessHours={enableBusinessHours}
                setEnableBusinessHours={setEnableBusinessHours}
                hasValidationError={highlightValidationErrors && (validationErrorSections.has("offhours_action") || validationErrorSections.has("offhours_ivr_menu"))}
              />
            </div>
          </div>
        ) : (
          <div className="w-full lg:w-[740px]">
            <CallAction
              route={route}
              setRoute={setRoute}
              setOpenSnack={setOpenSnack}
              setSnackMsg={setSnackMsg}
              setLoader={setLoader}
              enableBusinessHours={enableBusinessHours}
              setEnableBusinessHours={setEnableBusinessHours}
              hasValidationError={highlightValidationErrors && (validationErrorSections.has("primary_action") || validationErrorSections.has("ivr_menu"))}
            />
          </div>
        )}

        {/* Flow Arrows: Call Actions → Notifications */}
        {enableBusinessHours ? (
          <div className="lg:px-10 w-full">
            <div className="lg:grid grid-cols-2 gap-2 -mt-4">
              <div className="flex justify-center">
                <ArrowDown size={16} className="text-gray-400" />
              </div>
              <div className="flex justify-center">
                <ArrowDown size={16} className="text-gray-400" />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center -mt-4">
            <ArrowDown size={16} className="text-gray-400" />
          </div>
        )}
        
        {enableBusinessHours ? (
          <div className=" lg:px-10 w-full">
            <div className="text-lg font-semibold text-gray-900 mb-2">
              Notification
            </div>
            <div className="lg:grid grid-cols-2  items-start gap-2 ">
              <PrimaryNotification
                route={route}
                setRoute={setRoute}
                setOpenSnack={setOpenSnack}
                setSnackMsg={setSnackMsg}
                setLoader={setLoader}
                enableBusinessHours={enableBusinessHours}
                setEnableBusinessHours={setEnableBusinessHours}
                hasValidationError={highlightValidationErrors && validationErrorSections.has("primary_notification")}
              />
              <OffHoursNotification
                route={route}
                setRoute={setRoute}
                setOpenSnack={setOpenSnack}
                setSnackMsg={setSnackMsg}
                setLoader={setLoader}
                enableBusinessHours={enableBusinessHours}
                setEnableBusinessHours={setEnableBusinessHours}
                hasValidationError={highlightValidationErrors && validationErrorSections.has("offhours_notification")}
              />
            </div>
          </div>
        ) : (
          <Notification
            route={route}
            setRoute={setRoute}
            setOpenSnack={setOpenSnack}
            setSnackMsg={setSnackMsg}
            setLoader={setLoader}
            enableBusinessHours={enableBusinessHours}
            setEnableBusinessHours={setEnableBusinessHours}
            hasValidationError={highlightValidationErrors && validationErrorSections.has("primary_notification")}
          />
        )}
      </div>
      </>
      )}

      {/* error snack */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={openSnack}
        onClose={() => setOpenSnack(false)}
        autoHideDuration={snackMsg.length > 160 ? 14000 : 6000}
        message={<span style={{ whiteSpace: "pre-line" }}>{snackMsg}</span>}
        key={snackMsg}
      />
    </div>
    </RouteConfigModalsProvider>
  );
};

export default NewCallRoute;
